/**
 * Hooks for Outbound Email Templates API.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from "@/lib/api-client";
import { toast } from "@/lib/toast-service";
import type {
  OutboundEmailTemplateCreate,
  OutboundEmailTemplateListFilters,
  OutboundEmailTemplateResponse,
  OutboundEmailTemplateUpdate,
  TemplateCompaniesResponse,
} from "@/types/api/outbound-email-template";

const BASE = "/outbound-email-templates";

const QUERY_KEYS = {
  all: ["outbound-email-templates"] as const,
  lists: () => [...QUERY_KEYS.all, "list"] as const,
  list: (filters?: OutboundEmailTemplateListFilters) =>
    [...QUERY_KEYS.lists(), filters ?? {}] as const,
  details: () => [...QUERY_KEYS.all, "detail"] as const,
  detail: (id: string) => [...QUERY_KEYS.details(), id] as const,
  companies: (id: string) => [...QUERY_KEYS.detail(id), "companies"] as const,
};

interface ListResponse {
  items: OutboundEmailTemplateResponse[];
}

function buildParams(filters?: OutboundEmailTemplateListFilters): Record<string, string | boolean> {
  if (!filters) return {};
  const params: Record<string, string | boolean> = {};
  if (filters.status != null) params.status = filters.status;
  if (filters.company_id != null) params.company_id = filters.company_id;
  if (filters.system_only === true) params.system_only = true;
  return params;
}

export function useOutboundEmailTemplates(filters?: OutboundEmailTemplateListFilters) {
  return useQuery({
    queryKey: QUERY_KEYS.list(filters),
    queryFn: async (): Promise<OutboundEmailTemplateResponse[]> => {
      const data = await apiGet<ListResponse>(BASE, {
        requireAuth: true,
        params: buildParams(filters),
      });
      return data.items;
    },
    staleTime: 2 * 60 * 1000,
  });
}

export function useOutboundEmailTemplate(templateId: string | null) {
  return useQuery({
    queryKey: QUERY_KEYS.detail(templateId ?? ""),
    queryFn: async (): Promise<OutboundEmailTemplateResponse> => {
      return await apiGet<OutboundEmailTemplateResponse>(`${BASE}/${templateId}`, {
        requireAuth: true,
      });
    },
    enabled: !!templateId,
    staleTime: 2 * 60 * 1000,
  });
}

export function useCreateOutboundEmailTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: OutboundEmailTemplateCreate): Promise<OutboundEmailTemplateResponse> => {
      return await apiPost<OutboundEmailTemplateResponse>(BASE, data, { requireAuth: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.lists() });
      toast.success("Template creato con successo");
    },
  });
}

export function useUpdateOutboundEmailTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      templateId,
      data,
    }: {
      templateId: string;
      data: OutboundEmailTemplateUpdate;
    }): Promise<OutboundEmailTemplateResponse> => {
      return await apiPatch<OutboundEmailTemplateResponse>(`${BASE}/${templateId}`, data, {
        requireAuth: true,
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.detail(variables.templateId) });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.companies(variables.templateId),
      });
      toast.success("Template aggiornato con successo");
    },
  });
}

export function useDeleteOutboundEmailTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (templateId: string): Promise<void> => {
      return await apiDelete<void>(`${BASE}/${templateId}`, { requireAuth: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.lists() });
      toast.success("Template eliminato con successo");
    },
  });
}

export function useTemplateCompanies(templateId: string | null) {
  return useQuery({
    queryKey: QUERY_KEYS.companies(templateId ?? ""),
    queryFn: async (): Promise<TemplateCompaniesResponse> => {
      return await apiGet<TemplateCompaniesResponse>(`${BASE}/${templateId}/companies`, {
        requireAuth: true,
      });
    },
    enabled: !!templateId,
    staleTime: 2 * 60 * 1000,
  });
}

export function useSetTemplateCompanies(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (companyIds: string[]): Promise<TemplateCompaniesResponse> => {
      return await apiPut<TemplateCompaniesResponse>(`${BASE}/${templateId}/companies`, {
        company_ids: companyIds,
      }, { requireAuth: true });
    },
    onSuccess: (_data, _variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.detail(templateId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.companies(templateId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.lists() });
      toast.success("Associazioni companies aggiornate");
    },
  });
}

/** Set companies for any template (e.g. after create). Pass templateId in variables. */
export function useSetTemplateCompaniesById() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      templateId,
      companyIds,
    }: {
      templateId: string;
      companyIds: string[];
    }): Promise<TemplateCompaniesResponse> => {
      return await apiPut<TemplateCompaniesResponse>(
        `${BASE}/${templateId}/companies`,
        { company_ids: companyIds },
        { requireAuth: true }
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.detail(variables.templateId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.companies(variables.templateId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.lists() });
      toast.success("Associazioni companies aggiornate");
    },
  });
}
